import { useContext, useState } from 'react'

import clsx from 'clsx'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button from '@/components/view/atoms/button/Button'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { WidgetDisplayLogicModalContext } from './WidgetDisplayLogicModalProvider'

interface LogicItem {
  nameJa: string
  nameEn: string
  description: string
  note?: string
  placement: string
  // タグに渡す必要がある起点。記事レコメンドは商品IDと特集ページIDが混在するため、
  // 「商品IDが要るかどうか」の真偽値では表せない
  origin: '商品ID' | '特集ページID' | '不要'
}

interface LogicGroup {
  label: string
  // 起点を渡さずに置けるグループか。見出しの色分けに使う
  anywhere: boolean
  items: LogicItem[]
}

const LOGIC_GROUPS: LogicGroup[] = [
  {
    label: '商品詳細ページ向け',
    anywhere: false,
    items: [
      {
        nameJa: 'この商品を買った人はこちらも購入',
        nameEn: 'BB',
        description:
          '該当商品の購入者が他に購入した商品をスコア順に表示。過去90日の購入データ（タグ＋受注CSV）を集計。',
        note: 'item_id未送信のページではフォールバック（人気商品）が返される',
        placement: '商品詳細',
        origin: '商品ID'
      },
      {
        nameJa: 'この商品を見た人はこちらを購入',
        nameEn: 'OB',
        description: '該当商品の閲覧者が実際に購入した商品を表示。閲覧→購入のクロスセル発見に有効。',
        note: 'item_id未送信のページではフォールバック（人気商品）が返される',
        placement: '商品詳細',
        origin: '商品ID'
      },
      {
        nameJa: '類似商品',
        nameEn: 'Similar',
        description:
          '商品名・カテゴリ・説明文・タグをTF-IDFでベクトル化し、コサイン類似度の高い商品を表示。',
        note: 'item_id未送信のページではフォールバック（人気商品）が返される',
        placement: '商品詳細',
        origin: '商品ID'
      },
      {
        nameJa: 'この商品に関連する記事',
        nameEn: 'Article Related By Item',
        description:
          '閲覧中の商品と共通のAIタグを持つ記事（特集ページ）、および商品ベクトルに意味が近い記事を表示。商品詳細から特集ページへの送客に使う。',
        note: 'AIタグが未整備のサイトでは人気記事にフォールバックする',
        placement: '商品詳細',
        origin: '商品ID'
      }
    ]
  },
  {
    label: '特集ページ向け',
    anywhere: false,
    items: [
      {
        nameJa: 'この特集に関連する商品',
        nameEn: 'Related By Article',
        description:
          '閲覧中の特集と共通のAIタグを持つ商品、および特集ベクトルに意味が近い商品を表示。その特集に既に掲載されている商品は除いて出すため、CMSで組んだ商品グループの再掲にはならない。',
        note: 'AIタグが未整備のサイトでは人気商品にフォールバックする',
        placement: '特集ページ',
        origin: '特集ページID'
      },
      {
        nameJa: 'この特集を見た人はこちらも見ています',
        nameEn: 'Personal By Article',
        description:
          '閲覧中の特集を見た訪問者が、その後に閲覧・購入した商品を集計して表示。',
        note: '特集ページに閲覧コードの設置が必要。閲覧実績が無いと人気商品にフォールバックする',
        placement: '特集ページ',
        origin: '特集ページID'
      },
      {
        nameJa: '関連する記事',
        nameEn: 'Article Similar',
        description: '閲覧中の記事と共通のAIタグを持つ記事を、共通タグ数の多い順に表示。',
        note: 'AIタグが未整備のサイトでは人気記事にフォールバックする',
        placement: '特集ページ',
        origin: '特集ページID'
      }
    ]
  },
  {
    label: 'どのページでも利用可（TOP、マイページに最適）',
    anywhere: true,
    items: [
      {
        nameJa: 'パーソナライズド',
        nameEn: 'Personal',
        description:
          '閲覧・購入履歴をもとに協調フィルタリング・ニューラル・コンテンツベースの3アルゴリズムを組み合わせて表示。',
        note: '初回訪問者（行動履歴なし）はサイト内人気商品にフォールバック',
        placement: 'どのページでも可',
        origin: '不要'
      },
      {
        nameJa: '状況適応型',
        nameEn: 'Adaptive',
        description:
          'ユーザー状態（新規/閲覧のみ/購入済/休眠）を自動判定し最適なロジックを切替。1ウィジェットで全状態に対応。',
        note: 'item_idを渡すとカテゴリ文脈も考慮される',
        placement: 'どのページでも可',
        origin: '不要'
      },
      {
        nameJa: 'ランキング×パーソナル',
        nameEn: 'Ranking Blend',
        description:
          '閲覧数・購入数・カート数のランキングにパーソナルスコアをブレンド。新規ユーザーほどランキング寄り、会員ほどパーソナル寄りが自動調整。',
        note: 'category_idを渡すとカテゴリ内ランキングに絞られる',
        placement: 'トップ / カテゴリ / 特集',
        origin: '不要'
      },
      {
        nameJa: '最近見たものの続き',
        nameEn: 'Recently Viewed',
        description:
          '直近30日の閲覧履歴から類似商品を表示。「○○に似ています」という理由付きで表示。直近の閲覧ほど多く出力。',
        placement: 'トップ / マイページ',
        origin: '不要'
      },
      {
        nameJa: '人気の記事',
        nameEn: 'Article Trending',
        description: '閲覧数の多い記事（特集ページ）を順に表示。起点を必要としない。',
        placement: 'どのページでも可',
        origin: '不要'
      },
      {
        nameJa: 'この記事を読んだ人はこちらも読んでいます',
        nameEn: 'Article Personal',
        description:
          '訪問者が読んだ記事をもとに、同じ記事を読んだ人が他に読んでいる記事を協調フィルタリングで表示。',
        note: '特集ページに閲覧コードの設置が必要。閲覧実績が無いと人気記事にフォールバックする',
        placement: 'どのページでも可',
        origin: '不要'
      }
    ]
  },
  {
    label: 'メールに埋め込みが可能',
    anywhere: true,
    items: [
      {
        nameJa: 'メール配信向け',
        nameEn: 'Mail',
        description:
          'メール配信システムからAPI経由でレコメンドを取得する際に使用します。visitor_idまたはcustomer_idを指定して個別のレコメンドを返します。',
        note: 'ECサイトへのタグ埋め込みではなく、メール配信システムとのAPI連携で使用します。',
        placement: 'メルマガ・メール配信',
        origin: '不要'
      }
    ]
  }
]

const LogicItemRow = ({ item, anywhere }: { item: LogicItem; anywhere: boolean }) => (
  <div className='border-primary flex items-center gap-4 border-b py-3 last:border-b-0'>
    <div className='min-w-0 flex-1'>
      <div className='mb-3 flex items-center gap-2'>
        <Text size={16} color={anywhere ? TextColor.SUCCESS : TextColor.INFO} weight='bold'>
          {item.nameJa}
        </Text>
        <Text>{item.nameEn}</Text>
      </div>
      <Text size={12} tag='p'>
        {item.description}
      </Text>
      {item.note && (
        <div className='mt-2 flex items-start gap-1'>
          <IconText
            text={item.note}
            iconType={IconType.HELP}
            color={IconColor.CAUTION}
            textColor={TextColor.CAUTION}
            direction='horizon'
            textSize={12}
            iconLine
            noBold
          />
        </div>
      )}
    </div>
    <div className='flex w-32 shrink-0 items-center'>
      <Text size={12} nowrap>
        {item.placement}
      </Text>
    </div>
    <div className='flex w-28 shrink-0 justify-center'>
      <Badge
        badgeType={item.origin === '不要' ? BadgeType.SECONDARY : BadgeType.REQUIRED}
        size={10}
      >
        {item.origin}
      </Badge>
    </div>
  </div>
)

const AccordionSection = ({ group }: { group: LogicGroup }) => {
  const [open, setOpen] = useState<boolean>(true)

  return (
    <div>
      <button
        type='button'
        className={clsx(
          'border-t border-b',
          'flex w-full items-center justify-between px-4 py-2 text-left',
          group.anywhere
            ? 'border-t-success border-b-success bg-fill-success-secondary hover:bg-fill-success-secondary/80'
            : 'bg-fill-info-secondary hover:bg-fill-info-secondary/80 border-t-info border-b-info'
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <Text size={12} color={group.anywhere ? TextColor.SUCCESS : TextColor.INFO}>
          {group.label}
        </Text>
        <Icon type={open ? IconType.UP : IconType.DOWN} color={IconColor.INFO} size={16} />
      </button>
      <div className={`accordion ${open ? 'opening' : ''}`}>
        <div className='px-4'>
          {group.items.map((item, i) => (
            <LogicItemRow key={i} item={item} anywhere={group.anywhere} />
          ))}
        </div>
      </div>
    </div>
  )
}

const WidgetDisplayLogicModalInner = () => {
  return (
    <ModalContent type={ModalContentType.NO_FRAME}>
      <div className='fill-surface-secondary border-primary flex items-center gap-4 border-b px-4 py-2'>
        <div className='flex-1'>
          <Text size={12} color={TextColor.SECONDARY} weight='bold'>
            ロジック名
          </Text>
        </div>
        <div className='w-32 shrink-0'>
          <Text size={12} color={TextColor.SECONDARY} weight='bold'>
            設置箇所
          </Text>
        </div>
        <div className='w-28 shrink-0 text-center'>
          <Text size={12} color={TextColor.SECONDARY} weight='bold'>
            タグに渡す起点
          </Text>
        </div>
      </div>
      <div className='flex flex-col gap-0'>
        {LOGIC_GROUPS.map((group, i) => (
          <AccordionSection key={i} group={group} />
        ))}
      </div>
    </ModalContent>
  )
}

const WidgetDisplayLogicModal = () => {
  const { modal, showing, hide } = useContext(WidgetDisplayLogicModalContext)

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text={'レコメンドロジックについて'} onClickClose={hide} />
      {modal.showing && <WidgetDisplayLogicModalInner />}
      <ModalFooter>
        <Button color={'secondary'} size={'large'} onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default WidgetDisplayLogicModal
